import { t } from './i18n'

// Thin wrappers over Office.js Word. Each function opens its own `Word.run` so
// the caller doesn't have to know about the request-queue lifetime.
//
// Behavior notes:
// - Before the literal Word search, spaces (incl. NBSP and narrow NBSP) are
//   removed from both the anchor and the document text, so 50 000,00 matches
//   50\u00a0000,00. Word then searches for the original document slice, which
//   still has the document's own spaces.
// - When the first search misses we retry once with a trimmed anchor (first
//   40 chars) per SPEC-031.

/** True when Office.js is present and the required set is supported. */
export function isWord14Supported(): boolean {
  const office = (globalThis as unknown as { Office?: typeof Office }).Office
  if (!office?.context?.requirements) return false
  try {
    return office.context.requirements.isSetSupported('WordApi', '1.4')
  } catch {
    return false
  }
}

export async function getBodyOoxml(): Promise<string> {
  return Word.run(async (context) => {
    const ooxml = context.document.body.getOoxml()
    await context.sync()
    return ooxml.value
  })
}

export async function getBodyText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body
    body.load('text')
    await context.sync()
    return body.text
  })
}

export async function getSelectionText(): Promise<string> {
  return Word.run(async (context) => {
    const range = context.document.getSelection()
    range.load('text')
    await context.sync()
    return range.text
  })
}

export async function insertTextAtCursor(
  text: string,
  tracked = true,
): Promise<boolean> {
  try {
    await Word.run(async (context) => {
      const doc = context.document
      const range = doc.getSelection()
      if (!tracked) {
        range.insertText(text, Word.InsertLocation.replace)
        await context.sync()
        return
      }
      doc.load('changeTrackingMode')
      await context.sync()
      const previousMode = doc.changeTrackingMode
      try {
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll
        range.insertText(text, Word.InsertLocation.replace)
        await context.sync()
      } finally {
        doc.changeTrackingMode = previousMode
        await context.sync()
      }
    })
    return true
  } catch (err) {
    console.error('insertTextAtCursor failed', err)
    return false
  }
}

export interface LocateOptions {
  anchorText: string
  occurrenceIndex: number
}

/**
 * Locate a clause range by anchor text + occurrence and select it (Word will
 * scroll into view). Returns `true` on success. Callers use the return value
 * to fall back to the clipboard when the range is gone.
 */
export async function locateAndSelect(opts: LocateOptions): Promise<boolean> {
  if (!opts.anchorText) return false
  return Word.run(async (context) => {
    const range = await findRange(context, opts.anchorText, opts.occurrenceIndex)
    if (!range) return false
    range.select()
    await context.sync()
    return true
  })
}

export interface InsertCommentResult {
  ok: boolean
  reason?: 'range-miss' | 'unsupported' | 'failed'
}

/**
 * Insert a comment on the located range. SPEC-032: text is prefixed with
 * `[EUDOKIA] `. Word attributes the comment to the current user — we do not
 * (and cannot) claim EUDOKIA authorship.
 */
export async function insertCommentAt(
  opts: LocateOptions & { justification: string; findingType?: string },
): Promise<InsertCommentResult> {
  try {
    return await Word.run(async (context) => {
      const range = await findRange(context, opts.anchorText, opts.occurrenceIndex)
      if (!range) return { ok: false, reason: 'range-miss' as const }
      const text = formatJuniversoComment(opts.justification, opts.findingType)
      // range.insertComment requires WordApi 1.4 — the manifest gates that.
      range.insertComment(text)
      await context.sync()
      return { ok: true }
    })
  } catch (err) {
    console.error('insertComment failed', err)
    return { ok: false, reason: 'failed' }
  }
}

export interface CounterEditResult {
  ok: boolean
  reason?: 'failed'
  source?: 'selection' | 'cursor'
}

/**
 * Insert or replace at the current Word selection as a tracked change.
 * A non-empty selection is replaced (`replace` mode) or left intact
 * (`insert` mode). A collapsed cursor inserts in place. `insert` mode
 * starts a new paragraph at that point via `insertText`, so a two-column
 * table keeps the text in the cell under the cursor. `Paragraph.insertParagraph`
 * walks document order and lands in the next row's left cell.
 */
export async function insertCounterEditAt(opts: {
  replacementText: string
  insertMode?: 'replace' | 'insert'
}): Promise<CounterEditResult> {
  try {
    return await Word.run(async (context) => {
      const doc = context.document
      doc.load('changeTrackingMode')
      const selection = doc.getSelection()
      selection.load('text')
      await context.sync()

      const previousMode = doc.changeTrackingMode
      const selected = (selection.text ?? '').trim()
      const insertMode = opts.insertMode === 'insert' ? 'insert' : 'replace'
      try {
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll

        if (insertMode === 'insert') {
          selection.insertText(`\n${opts.replacementText}`, Word.InsertLocation.end)
          await context.sync()
          return { ok: true, source: 'cursor' as const }
        }

        selection.insertText(opts.replacementText, Word.InsertLocation.replace)
        await context.sync()
        return { ok: true, source: selected ? 'selection' as const : 'cursor' as const }
      } finally {
        doc.changeTrackingMode = previousMode
        await context.sync()
      }
    })
  } catch (err) {
    console.error('insertCounterEdit failed', err)
    return { ok: false, reason: 'failed' }
  }
}

const LOOSE_SEARCH = {
  matchCase: false,
  matchWholeWord: false,
  matchWildcards: false,
  ignorePunct: false,
  ignoreSpace: true,
  matchPrefix: false,
  matchSuffix: false,
}

const STRICT_SEARCH = {
  matchCase: true,
  matchWholeWord: false,
  matchWildcards: false,
  ignorePunct: false,
  ignoreSpace: false,
  matchPrefix: false,
  matchSuffix: false,
}

async function findRange(
  context: Word.RequestContext,
  anchorText: string,
  occurrenceIndex: number,
): Promise<Word.Range | null> {
  const body = context.document.body
  body.load('text')
  await context.sync()
  const verbatim = locateSpacelessMatch(anchorText, body.text ?? '', occurrenceIndex)
  if (verbatim) {
    const hit = await searchHit(context, verbatim.text, STRICT_SEARCH, verbatim.occurrence)
    if (hit) return hit
  }

  const needles = searchAnchorsToTry(anchorText)
  for (let i = 0; i < needles.length; i += 1) {
    const results = context.document.body.search(needles[i], LOOSE_SEARCH)
    results.load('items')
    await context.sync()
    const found = locateHitIndex(i, results.items.length, occurrenceIndex)
    if (found !== null) return results.items[found]
  }
  return null
}

async function searchHit(
  context: Word.RequestContext,
  needle: string,
  options: typeof LOOSE_SEARCH,
  occurrence: number,
): Promise<Word.Range | null> {
  const text = clip(needle)
  if (!text) return null
  const results = context.document.body.search(text, options)
  results.load('items')
  await context.sync()
  if (occurrence < 0 || occurrence >= results.items.length) return null
  return results.items[occurrence]
}

/** Primary needle may use occurrence_index. Shorter retries only if uniquely found. */
export function locateHitIndex(
  needleIndex: number,
  hitCount: number,
  occurrenceIndex: number,
): number | null {
  if (hitCount <= 0) return null
  if (needleIndex === 0) {
    return occurrenceIndex >= 0 && occurrenceIndex < hitCount ? occurrenceIndex : null
  }
  return hitCount === 1 ? 0 : null
}

/** Prefix for Office.js comments (author remains the Word user). */
export function formatJuniversoComment(
  justification: string,
  findingType?: string,
): string {
  const prefix =
    findingType === 'missing'
      ? t('[Відсутнє положення]')
      : findingType === 'atypical'
        ? t('[Нетипове положення]')
        : findingType === 'recurring'
          ? t('[Повторювана проблема]')
          : findingType === 'deviation'
            ? t('[Відхилення]')
            : '[EUDOKIA]'
  return `${prefix} ${justification}`
}

function isSearchSpace(ch: string): boolean {
  return ch === ' ' || ch === '\u00a0' || ch === '\u202f'
}

export function stripSearchSpaces(text: string): string {
  return text.replace(/[ \u00a0\u202f]/g, '')
}

export interface SpacelessHit {
  text: string
  occurrence: number
}

/**
 * Match an anchor against document text with spaces removed on both sides.
 * The returned text is the original document slice, including its real spaces,
 * so Word can search for it literally.
 */
export function locateSpacelessMatch(
  anchorText: string,
  documentText: string,
  occurrenceIndex: number,
): SpacelessHit | null {
  const needle = stripSearchSpaces(anchorText)
  if (!needle || occurrenceIndex < 0) return null
  const { stripped, origins } = spacelessIndex(documentText)
  let from = 0
  let seen = 0
  while (from <= stripped.length - needle.length) {
    const at = stripped.indexOf(needle, from)
    if (at < 0) return null
    if (seen === occurrenceIndex) {
      const start = origins[at]
      const end = origins[at + needle.length - 1] + 1
      const text = documentText.slice(start, end)
      return { text, occurrence: countExactBefore(documentText, text, start) }
    }
    seen += 1
    from = at + 1
  }
  return null
}

function spacelessIndex(text: string): { stripped: string; origins: number[] } {
  let stripped = ''
  const origins: number[] = []
  for (let i = 0; i < text.length; i += 1) {
    if (isSearchSpace(text[i])) continue
    stripped += text[i]
    origins.push(i)
  }
  return { stripped, origins }
}

function countExactBefore(text: string, slice: string, before: number): number {
  if (!slice) return 0
  let count = 0
  let from = 0
  while (from < before) {
    const at = text.indexOf(slice, from)
    if (at < 0 || at >= before) return count
    count += 1
    from = at + 1
  }
  return count
}

/** Anchors to try for Search: full, first 40, last 40, collapsed whitespace. */
export function searchAnchorsToTry(anchorText: string): string[] {
  if (!anchorText) return []
  const out: string[] = []
  const push = (raw: string) => {
    const next = clip(raw.trim())
    if (next && !out.includes(next)) out.push(next)
  }
  push(anchorText)
  push(anchorText.slice(0, 40))
  if (anchorText.length > 40) push(anchorText.slice(-40))
  const collapsed = anchorText.replace(/\s+/g, ' ').trim()
  if (collapsed !== anchorText.trim()) push(collapsed)
  return out
}

// Word search accepts up to 255 chars — anchors are ≤ 80 by SPEC-017 but clip
// defensively.
function clip(text: string): string {
  return text.length > 200 ? text.slice(0, 200) : text
}

/** Copy text to the clipboard with a best-effort textarea fallback. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch (err) {
    console.warn('clipboard.writeText failed, falling back', err)
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}
